<script setup>
import { Canvas, OrbitCameraComponent } from 'atlatl-visual-runtime';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';
import { ArrowBigLeftIcon, ArrowBigRightIcon } from 'vue-tabler-icons';
import { ref, markRaw, computed, defineProps } from 'vue';

const props = defineProps({ canvas: { type: Canvas, required: true } });

const store = useStore();
const route = useRoute();

const camera = ref(markRaw(props.canvas.getComponentByType(OrbitCameraComponent))).value;

const draft = computed(() => store.getters['ci/currentCIDraft']).value;

const defaultCamera = computed(() => draft?.scene?.camera?.default ?? {}).value;

function mapCameraValues({
  lowerAlphaLimit,
  upperAlphaLimit,
  alpha,
  lowerBetaLimit,
  upperBetaLimit,
  beta,
  radius,
  lowerRadiusLimit,
  upperRadiusLimit,
  nearClipDistance,
  farClipDistance,
  panningSensitivity,
  wheelPrecision,
  pinchPrecision,
  limitRadiusUsingBounds,
}) {
  return {
    options: {
      minimumAlpha: lowerAlphaLimit,
      maximumAlpha: upperAlphaLimit,
      initialAlpha: alpha,
      minimumBeta: lowerBetaLimit,
      maximumBeta: upperBetaLimit,
      initialBeta: beta,
      limitRadiusUsingBounds,
    },
    overrides: {
      initialRadius: radius,
      minimumRadius: lowerRadiusLimit,
      maximumRadius: upperRadiusLimit,
      nearClipDistance,
      farClipDistance,
      panningSensitivity,
      wheelPrecision,
      pinchPrecision,
    },
  };
}

function mapDraftCameraValues({ options, overrides }) {
  return {
    lowerAlphaLimit: options?.minimumAlpha,
    upperAlphaLimit: options?.maximumAlpha,
    alpha: options?.initialAlpha,
    lowerBetaLimit: options?.minimumBeta,
    upperBetaLimit: options?.maximumBeta,
    beta: options?.initialBeta,
    limitRadiusUsingBounds: options?.limitRadiusUsingBounds,
    radius: overrides?.initialRadius,
    lowerRadiusLimit: overrides?.minimumRadius,
    upperRadiusLimit: overrides?.maximumRadius,
    nearClipDistance: overrides?.nearClipDistance,
    farClipDistance: overrides?.farClipDistance,
    panningSensitivity: overrides?.panningSensitivity,
    wheelPrecision: overrides?.wheelPrecision,
    pinchPrecision: overrides?.pinchPrecision,
  };
}

function set() {
  Object.assign(defaultCamera, mapCameraValues(camera));
  store.commit('ci/setPreviewerOperation', {
    operations: [
      {
        component: 'scene.camera',
        operation: 'PUT',
        details: {
          name: 'default',
          values: {
            ...defaultCamera,
          },
        },
      },
    ],
    ciId: route.params.ciId,
  });
}

function apply() {
  Object.assign(camera, mapDraftCameraValues(defaultCamera));
}

if (Object.hasOwn(defaultCamera, 'options') && Object.hasOwn(defaultCamera, 'overrides')) {
  apply();
} else {
  Object.assign(defaultCamera, mapCameraValues(camera));
}
</script>

<template>
  <div class="flex items-center gap-4 rounded bg-white px-3 py-6">
    <span class="flex-grow"> Default </span>
    <button class="bg-blue-100" @click="set"><arrow-big-left-icon id="default-set" /> Set</button>
    <button class="bg-green-100" @click="apply">Apply <arrow-big-right-icon id="default-apply" /></button>
  </div>
</template>

<style lang="postcss" scoped>
button {
  @apply flex items-center gap-2 rounded px-2 py-1;
}
</style>
